
<template>
  <div class="page" :class="{ light: theme === 'light' }">
    <header class="nav">
      <span class="nav-brand">DANCELAB</span>
      <div class="nav-links">
        <router-link :to="{ name: 'index' }" class="nav-link" :class="{ 'is-active': $route.name === 'index' }">
          {{ lang === 'zh' ? '藏族舞者' : 'DANCER' }}
        </router-link>
        <span class="nav-sep">|</span>
        <router-link :to="{ name: 'home' }" class="nav-link" :class="{ 'is-active': $route.name === 'home' }">
          {{ lang === 'zh' ? '骨骼' : 'SKELETON' }}
        </router-link>
        <span class="nav-sep">|</span>
        <router-link :to="{ name: 'about' }" class="nav-link" :class="{ 'is-active': $route.name === 'about' }">
          {{ lang === 'zh' ? '项目介绍' : 'ABOUT' }}
        </router-link>
      </div>
      <div class="nav-controls">
        <div class="ctrl-lang" @click="toggleLang">
          <span :class="{ 'ctrl-active': lang === 'zh' }">ZH</span>
          <span class="ctrl-dot">·</span>
          <span :class="{ 'ctrl-active': lang === 'en' }">EN</span>
        </div>
        <div class="ctrl-theme" @click="toggleTheme">{{ theme === 'dark' ? '○' : '●' }}</div>
      </div>
    </header>

    <main class="content">
      <!-- HERO -->
      <section class="hero">
        <div class="dot-bg"></div>
        <div class="hero-inner">
          <div class="hero-version">DANCE LAB — AI · V2.0</div>
          <h1 class="hero-title">{{ lang === 'zh' ? '舞蹈数字动作库' : 'DANCE DIGITAL' }}<br>{{ lang === 'zh' ? '及智能分析系统' : 'MOTION LIBRARY' }}</h1>
          <p class="hero-sub">{{ lang === 'zh' ? '以西藏藏族男子热巴舞蹈为例，运用人工智能、光学运动捕捉技术进行动作采集与分析' : 'Using AI and optical motion capture technology to collect and analyse Tibetan male Reba dance movements' }}</p>
          <a class="whitepaper-link" href="https://drive.google.com/file/d/18mSZYxrtYIULvjM7Yn3CWBxR7uRrG1jx/view?usp=drive_link" target="_blank" rel="noopener">{{ lang === 'zh' ? '↓ DanceLab 白皮书 2026' : '↓ DanceLab Whitepaper 2026' }}</a>
          <div class="hero-video">
            <iframe
              src="https://player.bilibili.com/player.html?bvid=BV1DTPjz4EmD&autoplay=0&high_quality=1"
              scrolling="no"
              border="0"
              frameborder="no"
              framespacing="0"
              allowfullscreen="true"
            ></iframe>
          </div>
          <a class="yt-link" href="https://www.youtube.com/watch?v=Svvai9xhJec" target="_blank" rel="noopener">
            <svg class="yt-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <span>{{ lang === 'zh' ? '在 YouTube 观看' : 'Watch on YouTube' }}</span>
          </a>
        </div>
      </section>

      <!-- PROJECT INFO -->
      <section class="section">
        <div class="section-rule"></div>
        <h2 class="section-title">{{ lang === 'zh' ? '项目信息' : 'PROJECT INFO' }}</h2>
        <div class="stat-grid">
          <div class="stat-row">
            <span class="stat-label">{{ lang === 'zh' ? '项目负责人' : 'PRINCIPAL' }}</span>
            <span class="stat-value">{{ lang === 'zh' ? '吴振 · 北京舞蹈学院副教授，硕士生导师' : 'Wu Zhen · Associate Prof. & Master Supervisor, Beijing Dance Academy' }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">{{ lang === 'zh' ? '非遗传承人' : 'ICH INHERITOR' }}</span>
            <span class="stat-value">{{ lang === 'zh' ? '索朗亚次 · 藏族男子热巴舞传承人' : 'Solangyazi · Tibetan Male Reba Dance Inheritor' }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">{{ lang === 'zh' ? '项目组成员' : 'TEAM' }}</span>
            <span class="stat-value">{{ lang === 'zh' ? '杨浩 · 高原罗尔旦（西藏大学）· 桑嘎卓玛（西藏大学）' : 'Yang Hao · Gaoyuan Luoerdan (Tibet Univ.) · Sanga Zhuoma (Tibet Univ.)' }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">{{ lang === 'zh' ? '合作单位' : 'PARTNERS' }}</span>
            <span class="stat-value">{{ lang === 'zh' ? '西藏大学 · 科大讯飞' : 'Tibet University · iFLYTEK' }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">{{ lang === 'zh' ? '项目编号' : 'PROJECT NO.' }}</span>
            <span class="stat-value mono">KM202010051001</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">{{ lang === 'zh' ? '软件著作权' : 'COPYRIGHT' }}</span>
            <span class="stat-value mono">2023SR0604211</span>
          </div>
        </div>
      </section>

      <!-- DESCRIPTION -->
      <section class="section">
        <div class="section-rule"></div>
        <h2 class="section-title">{{ lang === 'zh' ? '项目概述' : 'OVERVIEW' }}</h2>
        <div class="desc-block">
          <p v-if="lang === 'zh'">
            该项目选取经国家级非物质文化遗产代表性藏族男子热巴舞蹈，邀请非物质文化传承人采集动作数据，通过生物力学分析软件进行运动分析，将运动捕捉系统与测力台相连，进行力学分析，以图像和量化数据等方式呈现。
          </p>
          <p v-else>
            This project captures nationally recognized intangible cultural heritage — Tibetan male Reba dance. Motion data is collected via optical motion capture by ICH inheritors, analyzed through biomechanical software and force-plate integration, and presented as quantified visual data for dance education.
          </p>
          <p v-if="lang === 'zh'" class="desc-second">
            结合光学运动捕捉技术和游戏引擎，搭建符合民族舞蹈创作、教学要求的实时渲染系统。2025 年在原版本基础上加入 AI 智能体，连接科大讯飞开发平台，开发非遗传承人索朗亚次 AI 数字人教师。
          </p>
          <p v-else class="desc-second">
            An AI agent was integrated in 2025, connecting to the iFLYTEK platform to develop Solangyazi — an AI digital human teacher embodying the ICH inheritor — enabling real-time interactive dance instruction at scale.
          </p>
        </div>
      </section>

      <!-- MILESTONES -->
      <section class="section">
        <div class="section-rule"></div>
        <h2 class="section-title">{{ lang === 'zh' ? '大事记' : 'MILESTONES' }}</h2>
        <div class="timeline-table">
          <div class="timeline-header">
            <span>{{ lang === 'zh' ? '时间' : 'DATE' }}</span>
            <span>{{ lang === 'zh' ? '事件' : 'EVENT' }}</span>
          </div>
          <div class="timeline-row" v-for="(row, i) in milestones" :key="i">
            <span class="tl-date">{{ row.date }}</span>
            <span class="tl-event">{{ lang === 'zh' ? row.zh : row.en }}</span>
          </div>
        </div>
      </section>

      <!-- DANCE CATEGORIES -->
      <section class="section">
        <div class="section-rule"></div>
        <h2 class="section-title">{{ lang === 'zh' ? '动作目录 · 西藏藏族男子热巴舞' : 'MOTION CATALOGUE · TIBETAN MALE REBA DANCE' }}</h2>
        <div class="catalogue">
          <div class="cat-part" v-for="(part, pi) in catalogue" :key="pi">
            <div class="cat-part-header">
              <span class="cat-roman">{{ part.roman }}</span>
              <span class="cat-part-title">{{ lang === 'zh' ? part.titleZh : part.titleEn }}</span>
            </div>
            <div class="cat-items">
              <div class="cat-item" v-for="(item, ii) in part.items" :key="ii">
                <span class="cat-num">{{ item.num }}</span>
                <span class="cat-name">{{ lang === 'zh' ? item.zh : item.en }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- DANCELAB DOWNLOAD -->
      <section class="section">
        <div class="section-rule"></div>
        <button class="dancelab-dl-btn" @click="showDlDialog = true">
          <span class="dancelab-dl-icon">↓</span>
          <span>{{ lang === 'zh' ? 'Dancelab 下载' : 'Dancelab Download' }}</span>
        </button>
      </section>

      <!-- CONTACT -->
      <section class="section contact-section">
        <div class="section-rule"></div>
        <h2 class="section-title">{{ lang === 'zh' ? '合作咨询' : 'CONTACT' }}</h2>
        <div class="contact-block">
          <div class="contact-row">
            <span class="contact-label">{{ lang === 'zh' ? '电话' : 'TEL' }}</span>
            <span class="contact-value">13810745684</span>
          </div>
          <div class="contact-row">
            <span class="contact-label">{{ lang === 'zh' ? '邮箱' : 'EMAIL' }}</span>
            <a class="contact-email" href="mailto:tot@alexzhenwu.com">tot@alexzhenwu.com</a>
          </div>
          <div class="contact-row">
            <span class="contact-label">{{ lang === 'zh' ? '作者网站' : 'WEBSITE' }}</span>
            <a class="contact-email" href="http://www.alexzhenwu.com" target="_blank">www.alexzhenwu.com</a>
          </div>
        </div>
      </section>
    </main>

    <!-- Download request dialog -->
    <div class="dl-overlay" v-if="showDlDialog" @click.self="showDlDialog = false">
      <div class="dl-dialog">
        <div class="dl-dialog-header">
          <span class="dl-dialog-title">{{ lang === 'zh' ? '下载申请' : 'DOWNLOAD REQUEST' }}</span>
          <button class="dl-dialog-close" @click="showDlDialog = false">✕</button>
        </div>
        <p class="dl-dialog-desc">{{ lang === 'zh' ? '填写以下信息，我们将在新版本上线时第一时间与您联系' : 'Fill in the form below and we will notify you when a new version is released.' }}</p>
        <div class="dl-field">
          <label class="dl-label">{{ lang === 'zh' ? '姓名' : 'NAME' }}</label>
          <input class="dl-input" v-model="dlForm.name" :placeholder="lang === 'zh' ? '请输入您的姓名' : 'Your name'" />
        </div>
        <div class="dl-field">
          <label class="dl-label">{{ lang === 'zh' ? '专业背景' : 'BACKGROUND' }}</label>
          <textarea class="dl-input dl-textarea" v-model="dlForm.background" :placeholder="lang === 'zh' ? '请介绍您的专业背景，如：舞蹈教师、戏剧导演、艺术设计师…' : 'e.g. Dance teacher, theatre director, art designer…'"></textarea>
        </div>
        <div class="dl-field">
          <label class="dl-label">{{ lang === 'zh' ? '联系邮箱' : 'EMAIL' }}</label>
          <input class="dl-input" type="email" v-model="dlForm.email" :placeholder="lang === 'zh' ? '请输入您的邮箱地址' : 'Your email address'" />
        </div>
        <div class="dl-dialog-actions">
          <button class="dl-btn-cancel" @click="showDlDialog = false">{{ lang === 'zh' ? '取消' : 'CANCEL' }}</button>
          <button class="dl-btn-submit" @click="submitDlDialog">{{ lang === 'zh' ? '确认下载' : 'DOWNLOAD' }}</button>
        </div>
      </div>
    </div>

    <footer class="footer">
      <span>DANCELAB · {{ lang === 'zh' ? '舞蹈数字动作库及智能分析系统' : 'Dance Digital Motion Library & Intelligent Analysis System' }}</span>
      <span>{{ lang === 'zh' ? '北京市教委 2020 科技计划 · KM202010051001' : 'Beijing Education Commission 2020 Technology Project · KM202010051001' }}</span>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'about-page',
  computed: {
    theme () { return this.$store.state.theme },
    lang ()  { return this.$store.state.lang },
  },
  methods: {
    toggleTheme () { this.$store.commit('toggleTheme') },
    toggleLang ()  { this.$store.commit('toggleLang') },
    async submitDlDialog () {
      try {
        await fetch(window.EMAIL_FUNC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:       this.dlForm.name,
            background: this.dlForm.background,
            email:      this.dlForm.email,
            message:    'Dancelab 数据集下载申请',
          }),
        })
      } catch (e) { console.error('提交失败', e) }
      this.showDlDialog = false
      this.dlForm = { name: '', background: '', email: '' }
    },
  },
  data () {
    return {
      showDlDialog: false,
      dlForm: { name: '', background: '', email: '' },
      milestones: [
        { date: '2020',     zh: '北京市科技类课题立项，项目编号 KM202010051001，课题周期 2021—2023', en: 'Beijing Science & Technology project approved. Project No. KM202010051001 (2021–2023)' },
        { date: '2021.11',  zh: '完成"舞蹈数字动作库及分析系统 Dance Lab V1.0"开发，软著 2021SR1619706', en: 'Dance Lab V1.0 completed — Dance Digital Motion Library & Analysis System. Copyright 2021SR1619706' },
        { date: '2022.08',  zh: '"雪"热巴舞传承人索朗亚次数字人模型版权登记，登记号：国作登字-2022-F-10216906', en: 'Digital human model of Solangyazi ("Snow" Reba inheritor) copyright registered. No. 国作登字-2022-F-10216906' },
        { date: '2022.12',  zh: '荣获"2022年度中国电子视像行业协会科技创新奖——技术奖"', en: 'Awarded 2022 China Electronic Video Industry Association Technology Innovation Award' },
        { date: '2023.06',  zh: '完成线上版本"舞蹈动作信息管理系统 Dance Lab V2.0"，软著 2023SR0604211', en: 'Dance Lab V2.0 online version released — Dance Motion Management System. Copyright 2023SR0604211' },
        { date: '2024.05',  zh: '完成"AI数字人模块"——数智舞蹈教学平台，连接科大讯飞开发平台', en: 'AI Digital Human module completed — intelligent dance teaching platform, integrated with iFLYTEK' },
        { date: '2026.05',  zh: '完成数据集搭建', en: 'Dataset construction completed' },
      ],
      catalogue: [
        {
          roman: 'Ⅰ',
          titleZh: '仪式',
          titleEn: 'RITUAL',
          items: [
            { num: '01', zh: '洗礼', en: 'Purification' },
            { num: '02', zh: '敬灵', en: 'Spirit Offering' },
            { num: '03', zh: '热谐（念白）', en: 'Recitation' },
          ],
        },
        {
          roman: 'Ⅱ',
          titleZh: '纯舞短句',
          titleEn: 'PURE DANCE PHRASES',
          items: [
            { num: '04', zh: '恰嘎（三点鼓）连接动作', en: 'Linking Move & 3-Beat I' },
            { num: '05', zh: '连接动作与三点鼓 2', en: 'Linking Move & 3-Beat II' },
            { num: '06', zh: '三点鼓蹦子', en: '3-Beat Drum Jump' },
            { num: '07', zh: '三点鼓蹲转', en: '3-Beat Drum Crouch Turn' },
            { num: '08', zh: '六点鼓', en: '6-Beat Drum' },
            { num: '09', zh: '九点鼓冈连（步态连九点鼓）', en: '9-Beat Drum — Ganglian' },
            { num: '10', zh: '九点鼓内外圈', en: '9-Beat Drum — Inner/Outer' },
            { num: '11', zh: '九点鼓冈果（原地转圈）', en: '9-Beat Drum — Gangguo' },
            { num: '12', zh: '连三九点鼓', en: 'Triple 9-Beat Drum' },
          ],
        },
        {
          roman: 'Ⅲ',
          titleZh: '传统短句',
          titleEn: 'TRADITIONAL PHRASES',
          items: [
            { num: '13', zh: '古冬仓措（九点鼓节奏）', en: 'Gudong Cangcuo' },
            { num: '14', zh: '梅朵安羌姆', en: 'Meiduo A-Qiangmu' },
            { num: '15', zh: '松梅丙羌姆', en: 'Songmei Binqiang' },
            { num: '16', zh: '铃', en: 'Bell' },
          ],
        },
      ],
    }
  },
  mounted () {
    document.title = 'DanceLab — 项目介绍'
  },
}
</script>

<style scoped>
/* ── TOKENS ── */
.page {
  --black: #000000;
  --surface: #111111;
  --surface-raised: #1a1a1a;
  --border: #222222;
  --border-v: #333333;
  --text-dis: #666666;
  --text-sec: #999999;
  --text-pri: #e8e8e8;
  --text-disp: #ffffff;
  --accent: #d71921;
  --ease: 200ms cubic-bezier(0.25, 0.1, 0.25, 1);
}

.page.light {
  --black: #f5f5f5;
  --surface: #ffffff;
  --surface-raised: #f0f0f0;
  --border: #e8e8e8;
  --border-v: #cccccc;
  --text-dis: #999999;
  --text-sec: #666666;
  --text-pri: #1a1a1a;
  --text-disp: #000000;
}

* { margin: 0; padding: 0; box-sizing: border-box; }

.page {
  min-height: 100vh;
  background: var(--black);
  font-family: 'Space Grotesk', 'DM Sans', system-ui, sans-serif;
  color: var(--text-pri);
  display: flex;
  flex-direction: column;
  transition: background var(--ease), color var(--ease);
}

/* ── NAV ── */
.nav {
  height: 48px;
  background: var(--black);
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  padding: 0 32px;
  flex-shrink: 0;
  transition: background var(--ease), border-color var(--ease);
}

.nav-brand {
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 20px;
  color: var(--text-disp);
  letter-spacing: -0.01em;
  user-select: none;
  flex-shrink: 0;
}

.nav-links {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.nav-sep {
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: var(--border-v);
  margin: 0 14px;
}

.nav-link {
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--text-dis);
  text-decoration: none;
  transition: color var(--ease);
  white-space: nowrap;
}

.nav-link:hover { color: var(--text-sec); }
.nav-link.is-active { color: var(--text-disp); }

.nav-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: 24px;
  padding-left: 24px;
  border-left: 1px solid var(--border);
}

.ctrl-lang {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
}

.ctrl-lang span, .ctrl-theme {
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--text-dis);
  transition: color var(--ease);
}

.ctrl-lang:hover span { color: var(--text-sec); }
.ctrl-lang span.ctrl-active { color: var(--text-disp); }
.ctrl-dot { color: var(--border-v) !important; }

.ctrl-theme {
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  letter-spacing: 0;
  color: var(--text-sec);
  line-height: 1;
  transition: color var(--ease);
}
.ctrl-theme:hover { color: var(--text-disp); }

/* ── CONTENT ── */
.content {
  flex: 1;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  padding: 0 32px 64px;
}

/* ── HERO ── */
.hero {
  position: relative;
  padding: 64px 0 48px;
  overflow: hidden;
}

.dot-bg {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, var(--border-v) 0.5px, transparent 0.5px);
  background-size: 16px 16px;
  opacity: 0.15;
  pointer-events: none;
}

.hero-inner { position: relative; z-index: 1; }

.hero-version {
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  color: var(--accent);
  margin-bottom: 20px;
}

.hero-title {
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 400;
  color: var(--text-disp);
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin-bottom: 24px;
}

.hero-sub {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  font-weight: 300;
  color: var(--text-sec);
  line-height: 1.6;
  max-width: 560px;
}

.whitepaper-link {
  display: inline-block;
  margin-top: 14px;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--accent);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color var(--ease), opacity var(--ease);
}
.whitepaper-link:hover { border-bottom-color: var(--accent); opacity: 0.8; }


/* ── HERO VIDEO ── */
.hero-video {
  margin-top: 32px;
  position: relative;
  width: 100%;
  max-width: 700px;
  aspect-ratio: 16 / 9;
  background: var(--surface);
  border: 1px solid var(--border);
}

.hero-video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* ── YOUTUBE LINK ── */
.yt-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  text-decoration: none;
  color: var(--text-sec);
  transition: color var(--ease);
}

.yt-link:hover { color: #ff0000; }

.yt-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.yt-link span {
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
}

/* ── SECTIONS ── */
.section { margin-top: 48px; }

.section-rule {
  height: 1px;
  background: var(--border);
  margin-bottom: 24px;
}

.section-title {
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  color: var(--text-sec);
  margin-bottom: 20px;
}

/* ── STAT GRID ── */
.stat-grid { display: flex; flex-direction: column; }

.stat-row {
  display: flex;
  align-items: baseline;
  gap: 24px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
}

.stat-row:first-child { border-top: 1px solid var(--border); }

.stat-label {
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--text-sec);
  white-space: nowrap;
  min-width: 120px;
  flex-shrink: 0;
}

.stat-value {
  font-size: 14px;
  color: var(--text-pri);
  line-height: 1.4;
}

.stat-value.mono {
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--text-disp);
}

/* ── DESCRIPTION ── */
.desc-block { max-width: 700px; }

.desc-block p {
  font-size: 15px;
  font-weight: 300;
  color: var(--text-pri);
  line-height: 1.7;
}

.desc-second {
  margin-top: 16px;
  color: var(--text-sec) !important;
}

/* ── TIMELINE TABLE ── */
.timeline-table { border-top: 1px solid var(--border); }

.timeline-header {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 32px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-v);
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--text-dis);
}

.timeline-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 32px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
  transition: background var(--ease);
}

.timeline-row:hover { background: var(--surface); }

.tl-date {
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--text-disp);
  white-space: nowrap;
  align-self: start;
}

.tl-event {
  font-size: 14px;
  font-weight: 300;
  color: var(--text-pri);
  line-height: 1.5;
}

/* ── CATALOGUE ── */
.catalogue { display: flex; flex-direction: column; gap: 24px; }

.cat-part {
  border: 1px solid var(--border);
}

.cat-part-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.cat-roman {
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--accent);
}

.cat-part-title {
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--text-sec);
}

.cat-items {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.cat-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 10px 16px;
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.cat-item:nth-child(3n) { border-right: none; }
.cat-item:last-child,
.cat-item:nth-last-child(-n+3):nth-child(3n+1) { border-bottom: none; }

.cat-num {
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: var(--text-dis);
  flex-shrink: 0;
}

.cat-name {
  font-size: 13px;
  color: var(--text-pri);
}

/* ── CONTACT ── */
.contact-section { margin-bottom: 0; }

.contact-block {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border);
}

.contact-row {
  display: flex;
  align-items: baseline;
  gap: 24px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
}

.contact-label {
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--text-sec);
  white-space: nowrap;
  min-width: 120px;
  flex-shrink: 0;
}

.contact-value {
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--text-disp);
}

.contact-email {
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--text-disp);
  text-decoration: none;
  transition: color var(--ease);
}

.contact-email:hover {
  color: var(--accent);
}

/* ── FOOTER ── */
.footer {
  border-top: 1px solid var(--border);
  padding: 12px 32px;
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-shrink: 0;
}

.footer span {
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.05em;
  color: var(--text-dis);
}

@media only screen and (max-width: 640px) {
  .content { padding: 0 16px 48px; }
  .hero { padding: 40px 0 32px; }
  .cat-items { grid-template-columns: repeat(2, 1fr); }
  .cat-item:nth-child(3n) { border-right: 1px solid var(--border); }
  .cat-item:nth-child(2n) { border-right: none; }
  .footer { flex-direction: column; gap: 4px; align-items: center; }
}

@media only screen and (max-width: 480px) {
  /* nav */
  .nav { padding: 0 12px; flex-wrap: wrap; height: auto; min-height: 48px; }
  .nav-brand { font-size: 15px; }
  .nav-links { margin-left: 0; width: 100%; justify-content: center; padding: 4px 0; }
  .nav-sep { margin: 0 8px; }
  .nav-link { font-size: 10px; }
  .nav-controls { margin-left: auto; padding-left: 12px; border-left: none; }

  /* hero */
  .content { padding: 0 14px 40px; }
  .hero { padding: 28px 0 24px; }
  .hero-version { font-size: 9px; }
  .hero-title { font-size: 28px; }
  .hero-sub { font-size: 13px; }
  .hero-video iframe { height: 52vw; }

  /* sections */
  .section-title { font-size: 11px; }
  .stat-row { flex-direction: column; gap: 4px; padding: 10px 0; }
  .stat-label { width: auto; }
  .stat-value { font-size: 12px; }

  /* catalogue */
  .cat-items { grid-template-columns: repeat(2, 1fr); }
  .cat-item:nth-child(3n) { border-right: 1px solid var(--border); }
  .cat-item:nth-child(2n) { border-right: none; }

  /* contact */
  .contact-row { flex-direction: column; gap: 4px; padding: 10px 0; }
  .contact-label { width: auto; }

  /* footer */
  .footer { padding: 8px 14px; gap: 4px; }
  .footer span { font-size: 9px; text-align: center; }

  /* download button */
  .dancelab-dl-btn { width: 100%; justify-content: center; }
}

/* ── DANCELAB DOWNLOAD BUTTON ── */
.dancelab-dl-btn {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 12px;
  background: none; border: 1px solid var(--border-v);
  color: var(--text-sec); cursor: pointer;
  font-family: 'Space Mono', monospace; font-size: 11px; letter-spacing: 0.06em;
  padding: 8px 16px;
  transition: border-color var(--ease), color var(--ease), background var(--ease);
}
.dancelab-dl-btn:hover { border-color: var(--accent); color: var(--accent); background: rgba(215,25,33,0.05); }
.dancelab-dl-icon { font-size: 13px; }

/* ── DOWNLOAD DIALOG ── */
.dl-overlay {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(0, 0, 0, 0.72);
  display: flex; align-items: center; justify-content: center;
}
.dl-dialog {
  background: var(--surface); border: 1px solid var(--border-v);
  width: 440px; max-width: calc(100vw - 32px);
  padding: 28px 28px 24px;
}
.dl-dialog-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
}
.dl-dialog-title {
  font-family: 'Space Mono', monospace; font-size: 13px; letter-spacing: 0.08em; color: var(--text-disp);
}
.dl-dialog-close {
  background: none; border: none; font-size: 12px; color: var(--text-dis); cursor: pointer;
  transition: color var(--ease);
}
.dl-dialog-close:hover { color: var(--text-disp); }
.dl-dialog-desc {
  font-family: 'Space Grotesk', sans-serif; font-size: 12px; color: var(--text-sec);
  line-height: 1.6; margin-bottom: 20px;
  border-left: 2px solid var(--accent); padding-left: 10px;
}
.dl-field { margin-bottom: 16px; }
.dl-label {
  display: block; font-family: 'Space Mono', monospace; font-size: 10px;
  letter-spacing: 0.08em; color: var(--text-sec); margin-bottom: 6px;
}
.dl-input {
  width: 100%; background: var(--surface-raised); border: 1px solid var(--border);
  color: var(--text-pri); font-family: 'Space Grotesk', sans-serif; font-size: 13px;
  padding: 8px 10px; outline: none; transition: border-color var(--ease); resize: none;
}
.dl-input:focus { border-color: var(--border-v); }
.dl-textarea { height: 72px; }
.dl-dialog-actions {
  display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px;
}
.dl-btn-cancel {
  background: none; border: 1px solid var(--border); color: var(--text-dis);
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em;
  padding: 7px 16px; cursor: pointer; transition: border-color var(--ease), color var(--ease);
}
.dl-btn-cancel:hover { border-color: var(--border-v); color: var(--text-sec); }
.dl-btn-submit {
  background: var(--accent); border: 1px solid var(--accent); color: #fff;
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em;
  padding: 7px 20px; cursor: pointer; transition: opacity var(--ease);
}
.dl-btn-submit:hover { opacity: 0.85; }
</style>
